import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  LayoutDashboard, Users, Clock, DollarSign, Calendar, Receipt,
  Briefcase, Shield, BarChart3, MessageSquare, Tablet, RotateCcw,
  Settings, LogOut, ChevronLeft, ChevronRight, X, Lock, ShieldCheck,
  Cpu, Fingerprint, QrCode, Camera, ChevronDown, FileText,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { selectCompanyName } from '../../store/slices/companySlice';
import { selectUser } from '../../store/slices/authSlice';
import { selectSidebarOpen, setSidebarOpen } from '../../store/slices/uiSlice';
import { clearAuth } from '../../store/slices/authSlice';
import { clearCompany } from '../../store/slices/companySlice';
import { clearWorkspace } from '../../store/slices/workspaceSlice';
import { Avatar } from '../shared/AvatarWithInitials';
import { usePlan } from '../../hooks/usePlan';
import { authApi } from '../../api/axios';

const NAV_MAIN = [
  { to: '/dashboard',   icon: LayoutDashboard, label: 'Dashboard'                           },
  { to: '/employees',   icon: Users,           label: 'Employees'                           },
  { to: '/attendance',  icon: Clock,           label: 'Attendance'                          },
  { to: '/payroll',     icon: DollarSign,      label: 'Payroll',     feature: 'payroll'     },
  { to: '/leave',       icon: Calendar,        label: 'Leave'                               },
  { to: '/expense',     icon: Receipt,         label: 'Expense',     feature: 'expense'     },
  { to: '/recruitment', icon: Briefcase,       label: 'Recruitment', feature: 'recruitment' },
  { to: '/compliance',  icon: Shield,          label: 'Compliance',  feature: 'compliance'  },
];

const NAV_TAIL = [
  { to: '/chat',   icon: MessageSquare, label: 'Chat'   },
  { to: '/roster', icon: RotateCcw,    label: 'Roster' },
];

const DOCUMENTS_ITEMS = [
  { to: '/documents/templates', icon: FileText,   label: 'Templates'      },
  { to: '/documents/issue',     icon: FileText,   label: 'Issue Document' },
  { to: '/documents/material',  icon: FileText,   label: 'Issue Material' },
  { to: '/documents/full-final',icon: FileText,   label: 'Full & Final'   },
];

const PRODUCTS_ITEMS = [
  { to: '/products/punch-log', icon: Fingerprint, label: 'Punch Log'                                },
  { to: '/lens',               icon: Tablet,      label: 'Lens Devices', feature: 'selfie_attendance' },
  { soon: true,                icon: QrCode,      label: 'QR + GPS'                                 },
  { soon: true,                icon: Camera,      label: 'Camera + GPS'                             },
];

function FlatNavItem({ to, icon: Icon, label, feature, collapsed, onClick }) {
  const { hasFeature } = usePlan();
  const locked = feature ? !hasFeature(feature) : false;
  return (
    <NavLink to={to} onClick={onClick}
      className={({ isActive }) => cn(
        'flex items-center gap-3 rounded-[10px] transition-all duration-150 group relative',
        collapsed ? 'h-10 w-10 mx-auto justify-center' : 'h-9 px-3',
        isActive
          ? 'bg-[var(--accent-muted)] text-[var(--accent)]'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
        locked && 'opacity-50',
      )}>
      <Icon size={18} className="shrink-0"/>
      {!collapsed && <span className="text-[13px] font-medium flex-1">{label}</span>}
      {!collapsed && locked && <Lock size={11} className="shrink-0 text-[var(--text-tertiary)]"/>}
      {collapsed && (
        <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-[var(--text-primary)] text-[var(--text-inverted)] text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          {label}{locked ? ' 🔒' : ''}
        </div>
      )}
    </NavLink>
  );
}

function CollapsibleSection({ icon: Icon, label, rootPath, items, collapsed, onClose, feature }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasFeature } = usePlan();
  const sectionLocked = feature ? !hasFeature(feature) : false;
  const isActive = location.pathname === rootPath || location.pathname.startsWith(rootPath + '/');
  const [open, setOpen] = useState(isActive);

  // Auto-expand when navigating into this section
  useEffect(() => { if (isActive) setOpen(true); }, [location.pathname]);

  if (collapsed) {
    return (
      <button
        onClick={() => navigate(rootPath)}
        className={cn(
          'h-10 w-10 mx-auto flex items-center justify-center rounded-[10px] transition-all group relative',
          isActive
            ? 'bg-[var(--accent-muted)] text-[var(--accent)]'
            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
        )}>
        <Icon size={18}/>
        <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-[var(--text-primary)] text-[var(--text-inverted)] text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          {label}
        </div>
      </button>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen(v => !v)}
        className={cn(
          'w-full flex items-center gap-3 h-9 px-3 rounded-[10px] transition-all duration-150',
          isActive
            ? 'text-[var(--accent)]'
            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
        )}>
        <Icon size={18} className="shrink-0"/>
        <span className="text-[13px] font-medium flex-1 text-left">{label}</span>
        {sectionLocked && <Lock size={11} className="shrink-0 text-[var(--text-tertiary)] mr-0.5"/>}
        <ChevronDown size={13} className={cn('transition-transform duration-200 shrink-0', open && 'rotate-180')}/>
      </button>

      {open && (
        <div className="ml-6 mt-0.5 space-y-0.5 border-l border-[var(--border)] pl-2">
          {items.map((item, i) => {
            if (item.soon) {
              return (
                <div key={i} className="flex items-center gap-2 h-8 px-2 text-[var(--text-tertiary)] cursor-not-allowed opacity-60">
                  <item.icon size={14} className="shrink-0"/>
                  <span className="text-[12px] flex-1">{item.label}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--bg-hover)] font-medium">Soon</span>
                </div>
              );
            }
            const locked = item.feature ? !hasFeature(item.feature) : false;
            return (
              <NavLink key={item.to} to={item.to} end={!!item.exact} onClick={onClose}
                className={({ isActive: a }) => cn(
                  'flex items-center gap-2 h-8 px-2 rounded-lg transition-all duration-150',
                  a
                    ? 'bg-[var(--accent-muted)] text-[var(--accent)]'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
                  locked && 'opacity-50',
                )}>
                <item.icon size={14} className="shrink-0"/>
                <span className="text-[12px] flex-1">{item.label}</span>
                {locked && <Lock size={10}/>}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ collapsed, onClose }) {
  const dispatch    = useDispatch();
  const navigate    = useNavigate();
  const companyName = useSelector(selectCompanyName);
  const user        = useSelector(selectUser);
  const sidebarOpen = useSelector(selectSidebarOpen);

  const handleLogout = async () => {
    try { await authApi.post('/v1/auth/logout'); } catch { /* clear regardless */ }
    dispatch(clearAuth()); dispatch(clearCompany()); dispatch(clearWorkspace());
    navigate('/login');
  };

  return (
    <div className={cn('flex flex-col h-full bg-[var(--bg-card)] border-r border-[var(--border)] transition-all duration-200 relative', collapsed ? 'w-16' : 'w-60')}>
      {/* Brand */}
      <div className={cn('flex items-center h-14 px-4 border-b border-[var(--border)] shrink-0', collapsed && 'justify-center px-2')}>
        <div className="w-8 h-8 rounded-xl bg-[var(--accent)] flex items-center justify-center text-white text-sm font-bold shrink-0">
          {companyName?.charAt(0) || 'A'}
        </div>
        {!collapsed && <span className="ml-2.5 text-[14px] font-bold text-[var(--text-primary)] truncate">{companyName || 'AttendX'}</span>}
        {onClose && (
          <button onClick={onClose} className="ml-auto lg:hidden w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]">
            <X size={16}/>
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV_MAIN.map(item => (
          <FlatNavItem key={item.to} {...item} collapsed={collapsed} onClick={onClose}/>
        ))}

        <CollapsibleSection
          icon={FileText} label="Documents" rootPath="/documents"
          items={DOCUMENTS_ITEMS} collapsed={collapsed} onClose={onClose}
          feature="documents"
        />

        <FlatNavItem to="/reports" icon={BarChart3} label="Reports" collapsed={collapsed} onClick={onClose}/>

        {NAV_TAIL.map(item => (
          <FlatNavItem key={item.to} {...item} collapsed={collapsed} onClick={onClose}/>
        ))}

        <CollapsibleSection
          icon={Cpu} label="Products" rootPath="/products"
          items={PRODUCTS_ITEMS} collapsed={collapsed} onClose={onClose}
        />
      </nav>

      {/* Footer */}
      <div className="border-t border-[var(--border)] py-3 px-2 space-y-0.5 shrink-0">
        <NavLink to="/settings" onClick={onClose}
          className={({ isActive }) => cn(
            'flex items-center gap-3 rounded-[10px] transition-all',
            collapsed ? 'h-10 w-10 mx-auto justify-center' : 'h-9 px-3',
            isActive ? 'bg-[var(--accent-muted)] text-[var(--accent)]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
          )}>
          <Settings size={18}/>{!collapsed && <span className="text-[13px] font-medium">Settings</span>}
        </NavLink>
        <div className={cn('flex items-center rounded-[10px]', collapsed ? 'h-10 w-10 mx-auto justify-center' : 'h-11 px-3 gap-3')}>
          <Avatar name={user?.name} size="sm"/>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-[var(--text-primary)] truncate">{user?.name}</p>
              <p className="text-[11px] text-[var(--text-tertiary)]">{user?.role}</p>
            </div>
          )}
          {!collapsed && (
            <button onClick={handleLogout} className="text-[var(--text-tertiary)] hover:text-[var(--danger)] transition-colors">
              <LogOut size={15}/>
            </button>
          )}
        </div>
        {user?.role === 'SUPER_ADMIN' && (
          <NavLink to="/super-admin" onClick={onClose}
            className={({ isActive }) => cn(
              'flex items-center gap-3 rounded-[10px] transition-all mt-1',
              collapsed ? 'h-10 w-10 mx-auto justify-center' : 'h-9 px-3',
              isActive ? 'bg-[var(--accent-muted)] text-[var(--accent)]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
            )}>
            <ShieldCheck size={18}/>{!collapsed && <span className="text-[13px] font-medium">Super Admin</span>}
          </NavLink>
        )}
      </div>

      <button
        onClick={() => dispatch(setSidebarOpen(!sidebarOpen))}
        className="hidden lg:flex absolute -right-3 top-16 w-6 h-6 bg-[var(--bg-card)] border border-[var(--border)] rounded-full items-center justify-center text-[var(--text-secondary)] hover:text-[var(--accent)] shadow-sm z-10">
        {collapsed ? <ChevronRight size={12}/> : <ChevronLeft size={12}/>}
      </button>
    </div>
  );
}

export function Sidebar({ mobileOpen, onMobileClose }) {
  const sidebarOpen = useSelector(selectSidebarOpen);
  return (
    <>
      <div className="hidden lg:flex relative shrink-0">
        <SidebarContent collapsed={!sidebarOpen}/>
      </div>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onMobileClose}/>
          <div className="absolute left-0 top-0 bottom-0 w-64 shadow-[var(--shadow-lg)] fade-in">
            <SidebarContent collapsed={false} onClose={onMobileClose}/>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
